import _ from 'lodash'
import dayjs from 'dayjs'
import { Dispatch, SetStateAction, forwardRef, useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AppointmentTooltip, AppointmentTooltipProps } from '@devexpress/dx-react-scheduler-material-ui'
import { AppointmentModel } from '@devexpress/dx-react-scheduler'

import { AppBar, Box, Button, Chip, Dialog, Grid, IconButton, Slide, Stack, Toolbar, Tooltip } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import TimelapseIcon from '@mui/icons-material/Timelapse'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import BlockIcon from '@mui/icons-material/Block'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CancelIcon from '@mui/icons-material/Cancel'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PlaceIcon from '@mui/icons-material/Place'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'

import DoctorIcon from '@/components/ui/icons/Doctor'
import PatientIcon from '@/components/ui/icons/Patient'
import { DeleteScheduleDialog } from './DeleteScheduleDialog'

import { useCreateScheduleExceptionMutation, useDeleteScheduleMutation } from '@/core/services/schedule'
import { useAcceptMeetingLinkOrderMutation, useGetMeetingLinkOrdersQuery, useRefuseMeetingLinkOrderMutation, useUpdateMeetingLinkOrderMutation } from '@/core/services/order'
import { Slot } from '@/core/models/schedule.model'
import { OrderStatus } from '@/core/models/order.model'

import { CreateConsultationStepEnum, CreateConsultationStepper } from '@/pages/create-consultation/CreateConsultationStepper'
import OrderStatusStepper from './OrderStatusStepper'
import StartConsultationDialog from './StartConsultationDialog'
import { SchedulerDataType } from '../enums/scheduler-data-type.enum'
import { Staff } from '@/core/models/staff.model'
import { useCurrentTime } from '@/hooks/useCurrentTime'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useAppSelector } from '@/redux/rootReducer'
import PdsIcon from '@/components/ui/icons/Pds'
import { getApiErrorMessage } from '@/utils/errors'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" mountOnEnter unmountOnExit ref={ref} {...props} />
})

function ScheduleAvailableSlots({ appointmentData }: { appointmentData: AppointmentModel }) {
  const { t } = useTranslation()

  const { startDate, endDate } = appointmentData

  const currentTime = useCurrentTime()

  const [openDialog, setOpenDialog] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState<Pick<Slot, 'start' | 'end'> | null>(null)

  const consultationReason = appointmentData.consultationReason as string | undefined
  const createdBy = appointmentData.createdBy as Staff | undefined
  const duration = appointmentData.duration as number | undefined
  const busySlots = (appointmentData.busySlots ?? []) as Slot[]

  const createConsultationInitialValues = useMemo(
    () => ({ medecin: createdBy?._id, medecinScheduleConsultationReason: consultationReason, startDate: selectedSlot?.start }),
    [consultationReason, createdBy, selectedSlot]
  )

  const slots = useMemo(() => {
    let slots: Pick<Slot, 'start' | 'end'>[] = []
    if (!duration) return slots

    const numberSlots = dayjs(endDate).diff(startDate, 'minutes') / duration

    for (let i = 0; i < numberSlots; i++) {
      const start = dayjs(startDate).add(i * duration, 'minute')
      const end = start.clone().add(duration, 'minute')
      const slot = {
        start: start.toISOString(),
        end: end.toISOString(),
      }
      if (
        !busySlots.some(
          (busySlot) => start.isBetween(busySlot.start, busySlot.end, undefined, '[]') || end.isBetween(busySlot.start, busySlot.end, undefined, '[]')
        )
      ) {
        slots.push(slot)
      }
    }
    return slots
  }, [duration, startDate, endDate, busySlots])

  return (
    <>
      <Grid className="Content-contentContainer" container alignItems="center">
        <Grid item xs={2} container alignItems="center" justifyContent="center">
          <EventAvailableIcon />
        </Grid>
        <Grid item xs={10}>
          <Stack flexDirection="row" flexWrap="wrap" gap={1}>
            {slots.map((slot, index) => (
              <Chip
                sx={[{ height: 35, backgroundColor: '#00DFC4' }]}
                key={`${slot.start} - ${index}`}
                disabled={currentTime.isAfter(slot.start)}
                label={dayjs(slot.start).isValid() ? dayjs(slot.start).format('HH[h]mm') : ''}
                onClick={() => {
                  setOpenDialog(true)
                  setSelectedSlot(slot)
                }}
              />
            ))}
          </Stack>
        </Grid>
      </Grid>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullScreen TransitionComponent={Transition}>
        <AppBar position="fixed">
          <Toolbar>
            <Tooltip title={<span>{t('tooltip.button.close')}</span>}>
              <IconButton color="inherit" onClick={() => setOpenDialog(false)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        <Toolbar />
        <CreateConsultationStepper
          config={{ showOnlyAvailability: true }}
          initialValues={createConsultationInitialValues}
          stepOrdinal={{
            [CreateConsultationStepEnum.SELECT_APPOINTMENT]: 0,
            [CreateConsultationStepEnum.SELECT_PATIENT]: 1,
            [CreateConsultationStepEnum.SELECT_SPECIALITY]: 2,
          }}
          onComplete={() => setOpenDialog(false)}
        />
      </Dialog>
    </>
  )
}

const orderStatusColors: Record<OrderStatus, string> = {
  [OrderStatus.Pending]: '#ffee58',
  [OrderStatus.Accepted]: '#66bb6a',
  [OrderStatus.Confirmed]: '#66bb6a',
  [OrderStatus.Refused]: '#ef5350',
  [OrderStatus.Canceled]: '#9e9e9e',
  [OrderStatus.In_Progress]: '#42a5f5',
  [OrderStatus.Consultation_Created]: '#42a5f5',
  [OrderStatus.Completed]: '#ff9800',
  [OrderStatus.No_Show]: '#9e9e9e',
  [OrderStatus.Rescheduled]: '#ab47bc',
}

function TooltipContent(props: AppointmentTooltip.ContentProps) {
  const { appointmentData } = props
  const { t } = useTranslation()

  const currentTime = useCurrentTime()

  if (appointmentData?.type === SchedulerDataType.ORDER) {
    const status = appointmentData.status as OrderStatus
    return (
      <Box sx={{ '& .Content-lens': { color: appointmentData?.color } }}>
        <AppointmentTooltip.Content {...props}>
          {appointmentData.patient && (
            <Grid className="Content-contentContainer" container alignItems="center">
              <Grid item xs={2} container alignItems="center" justifyContent="center">
                <PatientIcon />
              </Grid>
              <Grid item xs={10}>
                <span>{appointmentData.patient}</span>
              </Grid>
            </Grid>
          )}
          {appointmentData.speciality && (
            <Grid className="Content-contentContainer" container alignItems="center">
              <Grid item xs={2} container alignItems="center" justifyContent="center">
                <LocalHospitalIcon />
              </Grid>
              <Grid item xs={10}>
                <span>{appointmentData.speciality}</span>
              </Grid>
            </Grid>
          )}
          {appointmentData.meetingPlace && (
            <Grid className="Content-contentContainer" container alignItems="center">
              <Grid item xs={2} container alignItems="center" justifyContent="center">
                <PlaceIcon />
              </Grid>
              <Grid item xs={10}>
                <span>{appointmentData.meetingPlace}</span>
                {(() => {
                  const details = appointmentData.meetingPlaceDetails as { apartment?: string; floor?: string; doorCode?: string; instructions?: string } | undefined
                  if (!details) return null
                  const parts = [
                    details.apartment && `${t('page.appointments.detail.apartment')}: ${details.apartment}`,
                    details.floor && `${t('page.appointments.detail.floor')}: ${details.floor}`,
                    details.doorCode && `${t('page.appointments.detail.doorCode')}: ${details.doorCode}`,
                    details.instructions,
                  ].filter(Boolean)
                  if (parts.length === 0) return null
                  return <Box sx={{ mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>{parts.join(' · ')}</Box>
                })()}
              </Grid>
            </Grid>
          )}
          <Grid className="Content-contentContainer" container alignItems="center">
            <Grid item xs={2} />
            <Grid item xs={10}>
              <Chip
                label={t(`page.appointments.status.${status}`)}
                size="small"
                sx={{ backgroundColor: orderStatusColors[status], color: '#fff', fontWeight: 600 }}
              />
            </Grid>
          </Grid>
          <Box sx={{ px: 2, py: 1 }}>
            <OrderStatusStepper status={status} />
          </Box>
        </AppointmentTooltip.Content>
      </Box>
    )
  }

  const additionalContents = useMemo(() => {
    const createdBy = appointmentData?.createdBy as Staff | undefined

    return _.compact([
      appointmentData?.patient && { id: 'patient', icon: <PatientIcon />, text: appointmentData.patient },
      appointmentData?.doctor && { id: 'doctor', icon: <DoctorIcon />, text: appointmentData.doctor },
      createdBy && {
        id: 'createdBy',
        icon: createdBy.status === 'MEDECIN' ? <DoctorIcon /> : <PdsIcon />,
        text: `${createdBy.userInfo.name} ${createdBy.userInfo.surname}`,
      },
      appointmentData?.duration && { id: 'duration', icon: <TimelapseIcon />, text: `${appointmentData.duration} minutes` },
    ])
  }, [appointmentData])

  return (
    <Box
      sx={{
        '& .Content-lens': {
          color: appointmentData?.color,
        },
      }}
    >
      <AppointmentTooltip.Content {...props}>
        {additionalContents.map((content) => (
          <Grid key={content.id} className="Content-contentContainer" container alignItems="center">
            <Grid item xs={2} container alignItems="center" justifyContent="center">
              {content.icon}
            </Grid>
            <Grid item xs={10}>
              <span>{content.text}</span>
            </Grid>
          </Grid>
        ))}
        {(appointmentData?.createdBy as Staff | undefined)?.status === 'MEDECIN' &&
          appointmentData?.type === SchedulerDataType.SCHEDULE &&
          currentTime.isBefore(appointmentData.endDate) && <ScheduleAvailableSlots appointmentData={appointmentData} />}
      </AppointmentTooltip.Content>
    </Box>
  )
}

function OrderTooltipActions({ appointmentData, setVisible }: { appointmentData: AppointmentModel | undefined; setVisible: Dispatch<SetStateAction<boolean>> }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const authUser = useAppSelector(selectAuthUser)

  const [acceptOrder] = useAcceptMeetingLinkOrderMutation()
  const [refuseOrder] = useRefuseMeetingLinkOrderMutation()
  const [updateOrder] = useUpdateMeetingLinkOrderMutation()

  const [startDialogOpen, setStartDialogOpen] = useState(false)

  // Retrieve the full order from RTK Query cache (already fetched by AgendaScheduler)
  const orderId = appointmentData?.id as string | undefined
  const { order, isLoading: isOrderLoading } = useGetMeetingLinkOrdersQuery(
    { acceptedBy: authUser?._id },
    {
      skip: !authUser,
      selectFromResult: ({ data, isLoading }) => ({
        order: data?.find((o) => o._id === orderId),
        isLoading,
      }),
    }
  )

  if (!appointmentData) return null

  const status = appointmentData.status as OrderStatus

  const handleAccept = async () => {
    try {
      await acceptOrder(orderId!).unwrap()
      enqueueSnackbar(t('page.appointments.notification.accepted'), { variant: 'success' })
      setVisible(false)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.acceptError')), { variant: 'error' })
    }
  }

  const handleRefuse = async () => {
    try {
      await refuseOrder(orderId!).unwrap()
      enqueueSnackbar(t('page.appointments.notification.refused'), { variant: 'success' })
      setVisible(false)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.refuseError')), { variant: 'error' })
    }
  }

  const handleCancel = async () => {
    try {
      await updateOrder({ id: orderId!, status: OrderStatus.Canceled }).unwrap()
      enqueueSnackbar(t('page.appointments.notification.canceled'), { variant: 'success' })
      setVisible(false)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.cancelError')), { variant: 'error' })
    }
  }

  const handleConfirm = async () => {
    try {
      await updateOrder({ id: orderId!, status: OrderStatus.Confirmed }).unwrap()
      enqueueSnackbar(t('page.appointments.notification.confirmed'), { variant: 'success' })
      setVisible(false)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.confirmError')), { variant: 'error' })
    }
  }

  const handleNoShow = async () => {
    try {
      await updateOrder({ id: orderId!, status: OrderStatus.No_Show }).unwrap()
      enqueueSnackbar(t('page.appointments.notification.noShow'), { variant: 'success' })
      setVisible(false)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.noShowError')), { variant: 'error' })
    }
  }

  const handleComplete = async () => {
    try {
      await updateOrder({ id: orderId!, status: OrderStatus.Completed }).unwrap()
      enqueueSnackbar(t('page.appointments.notification.completed'), { variant: 'success' })
      setVisible(false)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.completeError')), { variant: 'error' })
    }
  }

  const handleOpenConsultation = () => {
    if (appointmentData.consultationId) {
      navigate(`/consultations/${appointmentData.consultationId}`)
    }
  }

  return (
    <Stack direction="row" spacing={0.5} sx={{ px: 2, pb: 1 }} flexWrap="wrap" gap={0.5}>
      {status === OrderStatus.Pending && (
        <>
          <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />} onClick={handleAccept}>
            {t('page.appointments.action.accept')}
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<BlockIcon />} onClick={handleRefuse}>
            {t('page.appointments.action.refuse')}
          </Button>
        </>
      )}
      {status === OrderStatus.Accepted && (
        <>
          <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />} onClick={handleConfirm}>
            {t('page.appointments.action.confirm')}
          </Button>
          <Button size="small" variant="contained" color="primary" startIcon={<PlayArrowIcon />} onClick={() => setStartDialogOpen(true)} disabled={isOrderLoading || !order}>
            {t('page.appointments.action.startConsultation')}
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancel}>
            {t('page.appointments.action.cancel')}
          </Button>
        </>
      )}
      {status === OrderStatus.Confirmed && (
        <>
          <Button size="small" variant="contained" color="primary" startIcon={<PlayArrowIcon />} onClick={() => setStartDialogOpen(true)} disabled={isOrderLoading || !order}>
            {t('page.appointments.action.startConsultation')}
          </Button>
          <Button size="small" variant="outlined" color="warning" startIcon={<EventBusyIcon />} onClick={handleNoShow}>
            {t('page.appointments.action.noShow')}
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancel}>
            {t('page.appointments.action.cancel')}
          </Button>
        </>
      )}
      {(status === OrderStatus.In_Progress || status === OrderStatus.Consultation_Created) && (
        <>
          <Button size="small" variant="contained" color="success" startIcon={<DoneAllIcon />} onClick={handleComplete}>
            {t('page.appointments.action.complete')}
          </Button>
          {appointmentData.consultationId && (
            <Button size="small" variant="outlined" color="primary" startIcon={<OpenInNewIcon />} onClick={handleOpenConsultation}>
              {t('page.appointments.action.openConsultation')}
            </Button>
          )}
        </>
      )}
      {order && (
        <StartConsultationDialog
          open={startDialogOpen}
          onClose={() => setStartDialogOpen(false)}
          order={order}
        />
      )}
    </Stack>
  )
}

function TooltipHeader(props: AppointmentTooltip.HeaderProps & { setVisible: Dispatch<SetStateAction<boolean>> }) {
  const { appointmentData, setVisible } = props

  const [createScheduleException] = useCreateScheduleExceptionMutation()
  const [deleteSchedule] = useDeleteScheduleMutation()

  const [open, setOpen] = useState(false)

  if (appointmentData?.type === SchedulerDataType.ORDER) {
    return (
      <>
        <AppointmentTooltip.Header
          {...props}
          showDeleteButton={false}
          showOpenButton={false}
        />
        <OrderTooltipActions appointmentData={appointmentData} setVisible={setVisible} />
      </>
    )
  }

  return (
    <>
      <AppointmentTooltip.Header
        {...props}
        showDeleteButton={!appointmentData?.disableDelete}
        onDeleteButtonClick={() => {
          setOpen(true)
        }}
      >
        {appointmentData?.link && (
          <IconButton component={Link} to={appointmentData.link} size="large">
            <OpenInNewRoundedIcon />
          </IconButton>
        )}
      </AppointmentTooltip.Header>
      <DeleteScheduleDialog
        appointmentData={appointmentData}
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={async ({ deleteType, deleteTo }) => {
          if (!appointmentData) return

          const { startDate, endDate, duration, recurrenceRule, scheduleId } = appointmentData

          if (!_.isString(scheduleId)) return

          try {
            const commonParams = {
              isCancelled: true,
              scheduleId: scheduleId,
              startDate: dayjs(startDate).format('YYYY-MM-DD'),
              startTime: dayjs(startDate).diff(dayjs(startDate).startOf('day'), 'minutes'),
            }
            switch (deleteType) {
              case '0':
                if (endDate && typeof duration === 'number') {
                  await createScheduleException({
                    ...commonParams,
                    duration,
                    endTime: dayjs(endDate).diff(dayjs(endDate).startOf('day'), 'minutes'),
                  }).unwrap()
                }
                break
              case '1':
                if (endDate && typeof duration === 'number' && deleteTo && recurrenceRule) {
                  await createScheduleException({
                    ...commonParams,
                    duration,
                    endDate: deleteTo,
                    endTime: dayjs(endDate).diff(dayjs(endDate).startOf('day'), 'minutes'),
                    recurrenceRule,
                  }).unwrap()
                }
                break
              case '2':
                if (endDate && typeof duration === 'number' && recurrenceRule) {
                  await createScheduleException({
                    ...commonParams,
                    duration,
                    endTime: dayjs(endDate).diff(dayjs(endDate).startOf('day'), 'minutes'),
                    recurrenceRule,
                  }).unwrap()
                }
                break
              // delete all schedules
              case '3':
                await deleteSchedule(scheduleId).unwrap()
                break
            }
            setOpen(false)
            setVisible(false)
          } catch (error) {}
        }}
      />
    </>
  )
}

export default function AgendaAppointmentTooltip(props: AppointmentTooltipProps) {
  const [visible, setVisible] = useState(false)

  const Header = useCallback((headerProps: AppointmentTooltip.HeaderProps) => {
    return <TooltipHeader {...headerProps} setVisible={setVisible} />
  }, [])

  return (
    <AppointmentTooltip
      {...props}
      contentComponent={TooltipContent}
      headerComponent={Header}
      showCloseButton
      visible={visible}
      onVisibilityChange={() => setVisible(!visible)}
    />
  )
}
